import { HierarchyUtils } from '@inter-ikea-core/core';
import { isDefined } from '../../utils/isDefined';
import { getSceneEntities } from './get-scene-manager';
import { getProductPartKey } from './product-part-entity-utils';
import { getProductType } from './product-info-entity-utils';
const isDisposedFilterChecker = filter => entity => {
    if (filter.in.length === 0) {
        return true;
    }
    const isDisposed = entity.isDisposed;
    return filter.in.includes(isDisposed);
};
const componentIdFilterChecker = filter => entity => {
    if (filter.in.length === 0) {
        return true;
    }
    return filter.in.some(value => entity.hasComponent(value));
};
const productTypeFilterChecker = filter => entity => {
    if (filter.in.length === 0) {
        return true;
    }
    const productType = getProductType(entity);
    return !!productType && filter.in.includes(productType);
};
const productPartKeyFilterChecker = filter => entity => {
    if (filter.in.length === 0) {
        return true;
    }
    const productPartKey = getProductPartKey(entity);
    return !!productPartKey && filter.in.includes(productPartKey);
};
const filterCheckers = {
    isDisposed: isDisposedFilterChecker,
    componentId: componentIdFilterChecker,
    productType: productTypeFilterChecker,
    productPartKey: productPartKeyFilterChecker
};
function assertUnreachable(_unexpectedValue) {
    // eslint-disable-next-line no-console
    console.error('Unexpected Case Value', _unexpectedValue);
    throw new Error(`The types must be wrong since an unexpected case is reachable`);
}
const ENTITY_FILTERS = {
    /**
     * @returns a filter definition for a function that will return true if and only if
     *   the entity it is provided is not disposed
     */
    isNotDisposed: () => ({
        has: 'isDisposed',
        in: [false]
    }),
    /**
     * @param {number | number[]} componentIds - a component id or an array of component ids to match against
     * @returns {FilterDefinition} a filter definition for a function that will return true if and only if
     *   the entity it is provided has matching component ids to any of the specified component ids
     */
    withMatchingComponentId: (componentIds) => ({
        has: 'componentId',
        in: Array.isArray(componentIds) ? componentIds : [componentIds]
    }),
    /**
     * @param {string | string[]} productTypes - a product type or an array of product types to match against
     * @returns {FilterDefinition} a filter definition for a function that will return true if and only if
     *    the entity it is provided has a matching product type to any of the specified product types
     */
    withMatchingProductType: (productTypes) => ({
        has: 'productType',
        in: Array.isArray(productTypes) ? productTypes : [productTypes]
    }),
    /**
     * @param {string | string[]} productTypes - a product part key or an array of product part keys to match against
     * @returns {FilterDefinition} a filter definition for a filter that will return true if and only if
     *    the entity it is provided has a matching product part key to any of the specified product types
     */
    withMatchingProductPartKey: (productPartKeys) => ({
        has: 'productPartKey',
        in: Array.isArray(productPartKeys) ? productPartKeys : [productPartKeys]
    })
};
const composeEntityMatchingFunction = (...filters) => (entity) => {
    const filtersArray = (Array.isArray(filters) ? filters : [filters]).filter(isDefined);
    return filtersArray.every(filter => {
        // return filterCheckers[filter.has](filter)(entity);
        // Annoying repetition for TS compiler...
        switch (filter.has) {
            case 'isDisposed': {
                return filterCheckers[filter.has](filter)(entity);
            }
            case 'componentId': {
                return filterCheckers[filter.has](filter)(entity);
            }
            case 'productType': {
                return filterCheckers[filter.has](filter)(entity);
            }
            case 'productPartKey': {
                return filterCheckers[filter.has](filter)(entity);
            }
            default: {
                assertUnreachable(filter);
            }
        }
    });
};
const composeEntityMatcher = (...filters) => {
    const match = composeEntityMatchingFunction(...filters);
    return {
        match,
        find: entities => entities.find(match) ?? null,
        filter: entities => entities.filter(match),
        findAncestor: entity => HierarchyUtils.getAncestors(entity).find(match) ?? null,
        findChild: entity => HierarchyUtils.children(entity).find(match) ?? null,
        findDescendant: entity => HierarchyUtils.descendants(entity).find(match) ?? null,
        findSceneEntity: sceneManagerOrCoreImpl => getSceneEntities(sceneManagerOrCoreImpl).find(match) ?? null,
        filterAncestors: entity => HierarchyUtils.getAncestors(entity).filter(match),
        filterChildren: entity => HierarchyUtils.children(entity).filter(match),
        filterDescendants: entity => HierarchyUtils.descendants(entity).filter(match),
        filterSceneEntities: sceneManagerOrCoreImpl => getSceneEntities(sceneManagerOrCoreImpl).filter(match)
    };
};
export const getProductTypeEntityMatcher = (productTypes) => composeEntityMatcher(ENTITY_FILTERS.withMatchingProductType(productTypes));
export const getProductPartKeyEntityMatcher = (productPartKeys) => composeEntityMatcher(ENTITY_FILTERS.withMatchingProductPartKey(productPartKeys));
export const getComponentEntityMatcher = (componentIds, { includeDisposed = false } = {}) => composeEntityMatcher(ENTITY_FILTERS.withMatchingComponentId(componentIds), includeDisposed ? undefined : ENTITY_FILTERS.isNotDisposed());
//# sourceMappingURL=entity-matcher-utils.js.map